/**
 * Teri's saved chats — list, resume, rename, delete — plus attachment staging.
 *
 * Everything here goes through the shared axios singleton. Only the chat
 * *stream* uses `fetch` (see services/assistant.ts and the reasoning in its
 * header); there is no reason for these plain JSON calls to opt out of the
 * tenant header and the 401 refresh interceptor.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosProgressEvent } from 'axios';
import toast from 'react-hot-toast';
import api from '../lib/api';
import type {
  AssistantAttachment,
  AssistantConversationDetail,
  AssistantConversationList,
  AssistantConversationSummary,
} from '../types/assistant';

interface Envelope<T> {
  status: string;
  data: T;
  message?: string;
}

export const CONVERSATIONS_PER_PAGE = 20;

/** Newest activity first, scoped to the caller's membership. */
export const listConversations = async (
  page = 1,
  perPage = CONVERSATIONS_PER_PAGE
): Promise<AssistantConversationList> => {
  const res = await api.get<Envelope<AssistantConversationList>>(
    '/assistant/conversations',
    { params: { page, per_page: perPage } }
  );
  return res.data.data;
};

/**
 * One conversation plus a page of its turns. Omit `before` for the newest
 * page; pass the oldest id you hold to page further back.
 */
export const getConversation = async (
  id: string,
  before?: string,
  limit = 50
): Promise<AssistantConversationDetail> => {
  const res = await api.get<Envelope<AssistantConversationDetail>>(
    `/assistant/conversations/${id}`,
    { params: { ...(before ? { before } : {}), limit } }
  );
  return res.data.data;
};

export const renameConversation = async (
  id: string,
  title: string
): Promise<AssistantConversationSummary> => {
  const res = await api.patch<Envelope<AssistantConversationSummary>>(
    `/assistant/conversations/${id}`,
    { title }
  );
  return res.data.data;
};

export const deleteConversation = async (id: string): Promise<void> => {
  await api.delete(`/assistant/conversations/${id}`);
};

export interface AssistantUploadOptions {
  onUploadProgress?: (event: AxiosProgressEvent) => void;
}

/**
 * Stage a file for the next message. One multipart call stores it and
 * extracts its content server-side; the returned id goes in
 * `ChatRequest.attachments`. Extracted text never comes back to the client.
 *
 * Called directly rather than through a `useMutation` wrapper so the progress
 * callback survives — the composer draws a per-file progress bar from it.
 */
export const uploadAssistantAttachment = async (
  file: File,
  options?: AssistantUploadOptions
): Promise<AssistantAttachment> => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<Envelope<AssistantAttachment>>(
    '/assistant/attachments',
    formData,
    {
      // Extraction (PDF/DOCX parsing, image downscaling) happens inside this
      // request, so the global 10s API timeout is far too tight for a 25 MB
      // document on a slow link.
      timeout: 120_000,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: options?.onUploadProgress,
    }
  );
  return res.data.data;
};

/** Discard a staged attachment. Only works while it is still unbound. */
export const deleteAssistantAttachment = async (id: string): Promise<void> => {
  await api.delete(`/assistant/attachments/${id}`);
};

// ─── Query keys ──────────────────────────────────────────────────────────

export const assistantKeys = {
  all: ['assistant'] as const,
  list: ['assistant', 'conversations'] as const,
  detail: (id: string) => ['assistant', 'conversation', id] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────

/**
 * The history list, page by page. Pass `enabled: false` until the panel is
 * open — an unopened Teri must not cost a request on every page load.
 *
 * Returns the query plus `conversations`, every loaded page flattened in
 * server order (most recently active first).
 */
export function useConversations(enabled = true) {
  const query = useInfiniteQuery({
    queryKey: assistantKeys.list,
    queryFn: ({ pageParam }: { pageParam: number }) => listConversations(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage: AssistantConversationList) =>
      lastPage.pagination.has_next ? lastPage.pagination.page + 1 : undefined,
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  const conversations: AssistantConversationSummary[] = (query.data?.pages ?? []).flatMap(
    (page) => page.conversations
  );

  return { ...query, conversations };
}

export function useConversation(id: string | null | undefined) {
  return useQuery({
    queryKey: assistantKeys.detail(id ?? ''),
    queryFn: () => getConversation(id as string),
    enabled: !!id,
    retry: false,
  });
}

export function useRenameConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      renameConversation(id, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assistantKeys.list });
    },
    onError: () => {
      toast.error('Could not rename this chat. Please try again.');
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteConversation(id),
    onSuccess: () => {
      toast.success('Chat deleted');
      queryClient.invalidateQueries({ queryKey: assistantKeys.list });
    },
    onError: () => {
      toast.error('Could not delete this chat. Please try again.');
    },
  });
}
